import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Slide,
  Snackbar,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import { useAlert } from "react-alert";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Group from "../../waybee-ui/Group";
import Button from "../../waybee-ui/Button";
import BackButton from "../../components/BackButton";
import JobInProgressLoading from "../../components/JobInProgressLoading";
import useJobQueueWaiting from "../../utils/useJobQueueWaiting";
import OccurrenceFilterControl from "../../components/occurrences/OccurrenceFilterControl";
import ClassStagesService, {
  NEW_STAGE_ID
} from "../../services/ClassStagesService";
import ClassStageForm from "./ClassStageForm";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 0 16px;
`;

const ToolbarFilters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
`;

const SearchField = styled(TextField)`
  flex: 1;
  min-width: 240px;
  max-width: 360px;
`;

const SlideDown = props => <Slide {...props} direction="down" />;

const linkMatches = (link, { classes = [], disciplines = [] }) =>
  (!classes.length || classes.includes(link.classId)) &&
  (!disciplines.length || disciplines.includes(link.disciplineName));

const uniqueOptions = (links, getValue, getLabel) =>
  [...new Map(links.map(link => [getValue(link), getLabel(link)]))]
    .filter(([value]) => value)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const StageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StageCard = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 14px 18px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  font: inherit;
  &:hover {
    border-color: #bdbdbd;
    background: #fafafa;
  }
`;

const StageLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const LinkChip = styled(Chip)`
  && {
    height: 28px;
    font-size: 13px;
  }
`;

const StageLinksHint = styled(Typography)`
  && {
    font-size: 13px;
    color: #777;
  }
`;

const StageTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 17px;
    color: #333;
  }
`;

const StageMeta = styled(Typography)`
  && {
    font-size: 13px;
    color: #777;
    margin-top: 2px;
  }
`;

const StageDescription = styled(Typography)`
  && {
    font-size: 14px;
    color: #444;
    margin-top: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-line;
  }
`;

const Message = styled(Typography)`
  && {
    color: #6b6b6b;
    text-align: center;
    margin: 24px 8px;
  }
`;

const ClassStages = ({ match, history }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const classId = match.params.id;
  const { stageId } = match.params;
  const [data, setData] = useState({ periods: [], stages: [] });
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    classes: [classId],
    disciplines: []
  });
  const [savedNotice, setSavedNotice] = useState(false);
  const { waiting, startWaiting } = useJobQueueWaiting(
    job => job.name === "save_class_stage" && job.classId === classId,
    () => setReloadKey(previous => previous + 1)
  );

  const showError = message => {
    alert.show(message, { title: t("common.actions.error") });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setHasError(false);
    ClassStagesService.getStages(classId)
      .then(result => {
        if (active) setData(result);
      })
      .catch(() => {
        if (!active) return;
        setHasError(true);
        showError(t("classStages.loadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [classId, reloadKey]);

  const links = useMemo(() => data.stages.flatMap(stage => stage.links), [
    data.stages
  ]);
  const classOptions = useMemo(
    () =>
      uniqueOptions(
        links,
        link => link.classId,
        link => link.className
      ),
    [links]
  );
  const disciplineOptions = useMemo(
    () =>
      uniqueOptions(
        links.filter(link => linkMatches(link, { classes: filters.classes })),
        link => link.disciplineName,
        link => link.disciplineName
      ),
    [links, filters.classes]
  );

  const visibleStages = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.stages.filter(
      stage =>
        (stage.title || "").toLowerCase().includes(term) &&
        stage.links.some(link => linkMatches(link, filters))
    );
  }, [data.stages, search, filters]);

  const changeFilter = (name, values) => {
    const next = { ...filters, [name]: values };
    setFilters({
      classes: next.classes,
      disciplines: next.classes.length
        ? next.disciplines.filter(discipline =>
            links.some(link =>
              linkMatches(link, {
                classes: next.classes,
                disciplines: [discipline]
              })
            )
          )
        : []
    });
  };

  const hasActiveFilters =
    search.trim() !== "" ||
    filters.classes.length > 0 ||
    filters.disciplines.length > 0;

  const renderSelect = (name, label, options) => (
    <OccurrenceFilterControl
      disabled={!options.length}
      style={{ maxWidth: 260 }}
    >
      <InputLabel id={`stage-${name}-filter`}>{label}</InputLabel>
      <Select
        multiple
        labelId={`stage-${name}-filter`}
        label={label}
        value={filters[name]}
        onChange={event => changeFilter(name, event.target.value)}
        renderValue={selected =>
          options
            .filter(option => selected.includes(option.value))
            .map(option => option.label)
            .join(", ")}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              size="small"
              color="primary"
              checked={filters[name].includes(option.value)}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </OccurrenceFilterControl>
  );

  const renderLinks = stage => {
    const current = stage.links.filter(link => link.classId === classId);
    const otherCount = stage.links.length - current.length;
    return (
      <StageLinks>
        {current.map(link => (
          <LinkChip
            key={link.idTurmaDisc}
            variant="outlined"
            label={link.disciplineName}
          />
        ))}
        {!current.length && (
          <StageLinksHint>
            {t("classStages.assignment.notLinked")}
          </StageLinksHint>
        )}
        {otherCount > 0 && (
          <LinkChip
            variant="outlined"
            label={t("classStages.assignment.otherClasses", {
              count: otherCount
            })}
          />
        )}
      </StageLinks>
    );
  };

  const openStage = id => {
    history.push(`/classes/${classId}/stages/${encodeURIComponent(id)}`);
  };

  const openNewStage = () => {
    if (hasError) {
      showError(t("classStages.loadError"));
      return;
    }
    if (!data.periods.length) {
      showError(t("classStages.form.noPeriods"));
      return;
    }
    openStage(NEW_STAGE_ID);
  };

  if (stageId && !loading && !hasError && !waiting) {
    const openedId = decodeURIComponent(stageId);
    return (
      <ClassStageForm
        key={openedId}
        stage={data.stages.find(stage => stage.id === openedId) || null}
        periods={data.periods}
        onSaved={() => {
          history.push(`/classes/${classId}/stages`);
          startWaiting();
          setSavedNotice(true);
        }}
      />
    );
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box textAlign="center" my={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (hasError) return <Message>{t("classStages.loadError")}</Message>;
    if (!data.stages.length) return <Message>{t("classStages.empty")}</Message>;
    if (!visibleStages.length) {
      return <Message>{t("classStages.filters.empty")}</Message>;
    }

    return (
      <StageList>
        {visibleStages.map(stage => (
          <StageCard
            key={stage.id}
            type="button"
            onClick={() => openStage(stage.id)}
          >
            <StageTitle>{stage.title}</StageTitle>
            <StageMeta>
              {[stage.stageCode, stage.periodName || stage.periodCode]
                .filter(Boolean)
                .join(" · ")}
            </StageMeta>
            <StageDescription>
              {stage.description || t("classStages.noDescription")}
            </StageDescription>
            {renderLinks(stage)}
          </StageCard>
        ))}
      </StageList>
    );
  };

  return (
    <Grid container spacing={2}>
      <BackButton to={`/classes/${classId}`} />
      <Grid item xs={12}>
        <Group fullWidth>
          {waiting ? (
            <JobInProgressLoading
              message={t("jobs.messages.pendingClassStage")}
            />
          ) : (
            <>
              <Toolbar>
                <ToolbarFilters>
                  <SearchField
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={t("classStages.filters.search")}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      )
                    }}
                  />
                  {renderSelect(
                    "classes",
                    t("classStages.filters.class"),
                    classOptions
                  )}
                  {filters.classes.length > 0 &&
                    renderSelect(
                      "disciplines",
                      t("classStages.filters.discipline"),
                      disciplineOptions
                    )}
                  {hasActiveFilters && (
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setFilters({ classes: [], disciplines: [] });
                      }}
                    >
                      {t("classStages.filters.clear")}
                    </Button>
                  )}
                </ToolbarFilters>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={loading}
                  onClick={openNewStage}
                >
                  <AddIcon />
                  {t("classStages.newStage")}
                </Button>
              </Toolbar>
              {renderContent()}
            </>
          )}
        </Group>
      </Grid>
      <Snackbar
        open={savedNotice}
        autoHideDuration={4000}
        onClose={() => setSavedNotice(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideDown}
      >
        <Alert
          variant="filled"
          severity="success"
          onClose={() => setSavedNotice(false)}
        >
          {t("classStages.form.saveQueued")}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

ClassStages.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      stageId: PropTypes.string
    }).isRequired
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired
};

export default withRouter(ClassStages);
